import { FC, ReactNode, useCallback, useEffect, useRef, useState, useContext } from "react";

import { WrapperLeft } from "@/app/application/WrapperLeft";
import { WrapperRight } from "@/app/application/WrapperRight";
import { Box, Grid, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

import { GetLanguageValueAsString } from "@/app/application/Translate";


interface PanelGridProps {
	rightPanel: ReactNode;
	leftPanel: ReactNode;
	/** Override left column size (e.g. { xs: 12, sm: 4 } for ~33%) */
	leftSize?: { xs?: number; sm?: number };
	/** Override right column size (e.g. { xs: 12, sm: 8 } for ~67%) */
	rightSize?: { xs?: number; sm?: number };
	leftPanelSx?: object;
	rightPanelSx?: object;
	containerSx?: object;
	resizableDesktop?: boolean;
	resizeStorageKey?: string;
	defaultLeftWidth?: number;
	minLeftWidth?: number;
	minRightWidth?: number;
}

export const PanelsGrid: FC<PanelGridProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const leftSize = props.leftSize ?? { xs: 12, sm: 6 };
	const rightSize = props.rightSize ?? { xs: 12, sm: 6 };
	const resizableDesktop = Boolean(props.resizableDesktop);
	const defaultLeftWidth = props.defaultLeftWidth ?? 420;
	const minLeftWidth = props.minLeftWidth ?? 320;
	const minRightWidth = props.minRightWidth ?? 420;
	const resizeStorageKey = props.resizeStorageKey;
	const [leftPanelWidth, setLeftPanelWidth] = useState<number>(() => {
		if (!resizableDesktop || !resizeStorageKey || typeof window === "undefined") {
			return defaultLeftWidth;
		}

		try {
			const saved = localStorage.getItem(resizeStorageKey);
			const parsed = saved ? Number(saved) : NaN;
			return Number.isFinite(parsed) ? parsed : defaultLeftWidth;
		} catch {
			return defaultLeftWidth;
		}
	});
	const isResizingRef = useRef(false);
	const resizeStartXRef = useRef(0);
	const resizeStartWidthRef = useRef(defaultLeftWidth);

	const panelShellSx = {
		height: "100%",
		minHeight: 0,
		display: "flex",
		flexDirection: "column",
		border: (panelTheme: typeof theme) => `1px solid ${panelTheme.palette.divider}`,
		borderRadius: 2,
		backgroundColor: "background.paper",
		overflow: "hidden",
		boxShadow: (panelTheme: typeof theme) =>
			panelTheme.palette.mode === "light"
				? "0 1px 2px rgba(15, 23, 42, 0.04)"
				: "none",
	};

	const getMaxLeftWidth = useCallback(() => {
		if (typeof window === "undefined") return defaultLeftWidth;
		return Math.max(minLeftWidth, window.innerWidth - minRightWidth);
	}, [defaultLeftWidth, minLeftWidth, minRightWidth]);

	const clampLeftWidth = useCallback(
		(width: number) =>
			Math.max(minLeftWidth, Math.min(getMaxLeftWidth(), width)),
		[getMaxLeftWidth, minLeftWidth],
	);

	useEffect(() => {
		if (!resizableDesktop || !resizeStorageKey) return;
		try {
			localStorage.setItem(resizeStorageKey, String(leftPanelWidth));
		} catch {
			// Ignore storage write failures in restricted environments.
		}
	}, [leftPanelWidth, resizeStorageKey, resizableDesktop]);

	useEffect(() => {
		if (!resizableDesktop || isMobile) return;

		const syncWidthToViewport = () => {
			setLeftPanelWidth((prev) => clampLeftWidth(prev));
		};

		syncWidthToViewport();
		window.addEventListener("resize", syncWidthToViewport);
		return () => window.removeEventListener("resize", syncWidthToViewport);
	}, [clampLeftWidth, isMobile, resizableDesktop]);

	const stopResize = useCallback(() => {
		if (!isResizingRef.current) return;
		isResizingRef.current = false;
		document.body.style.cursor = "";
		document.body.style.userSelect = "";
	}, []);

	const handleResizeStart = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (!resizableDesktop || isMobile) return;
			event.preventDefault();
			isResizingRef.current = true;
			resizeStartXRef.current = event.clientX;
			resizeStartWidthRef.current = leftPanelWidth;
			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
		},
		[isMobile, leftPanelWidth, resizableDesktop],
	);

	useEffect(() => {
		if (!resizableDesktop) return;

		const onPointerMove = (event: PointerEvent) => {
			if (!isResizingRef.current) return;
			const delta = event.clientX - resizeStartXRef.current;
			setLeftPanelWidth(clampLeftWidth(resizeStartWidthRef.current + delta));
		};
		const onPointerUp = () => stopResize();

		window.addEventListener("pointermove", onPointerMove);
		window.addEventListener("pointerup", onPointerUp);
		window.addEventListener("pointercancel", onPointerUp);
		return () => {
			window.removeEventListener("pointermove", onPointerMove);
			window.removeEventListener("pointerup", onPointerUp);
			window.removeEventListener("pointercancel", onPointerUp);
			stopResize();
		};
	}, [clampLeftWidth, resizableDesktop, stopResize]);

	if (resizableDesktop && !isMobile) {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "stretch",
					gap: 0,
					minWidth: 0,
					...(props.containerSx ?? {}),
				}}
			>
				<Box
					sx={{
						width: leftPanelWidth,
						minWidth: 0,
						flexShrink: 0,
						pr: 1,
						...props.leftPanelSx,
					}}
				>
					<Box sx={panelShellSx}>
						<WrapperLeft className="wrapper--small">
							{props.leftPanel}
						</WrapperLeft>
					</Box>
				</Box>

				<Box
					role="separator"
					aria-orientation="vertical"
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Ui.Components.PanelsGrid.L170C17.ResizeTrainingPanels", "Resize training panels")}
					onPointerDown={handleResizeStart}
					onDoubleClick={() => setLeftPanelWidth(clampLeftWidth(defaultLeftWidth))}
					sx={{
						width: 14,
						flexShrink: 0,
						cursor: "col-resize",
						touchAction: "none",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						userSelect: "none",
						"&::after": {
							content: "\"\"",
							width: 3,
							height: 64,
							borderRadius: 999,
							backgroundColor: alpha(theme.palette.divider, 0.8),
							transition: "background-color 120ms ease",
						},
						"&:hover::after": {
							backgroundColor: alpha(theme.palette.primary.main, 0.65),
						},
					}}
				/>

				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						pl: 1,
						...props.rightPanelSx,
					}}
				>
					<Box sx={panelShellSx}>
						<WrapperRight className="wrapper--half">
							{props.rightPanel}
						</WrapperRight>
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Grid
			container
			spacing={2}
			sx={{
				alignItems: "stretch",
				minWidth: 0,
				...(props.containerSx ?? {}),
			}}
		>
			<Grid
				item
				xs={leftSize.xs ?? 12}
				sm={leftSize.sm ?? 6}
				sx={{
					minWidth: 0,
					...props.leftPanelSx,
				}}
			>
				<Box sx={panelShellSx}>
					<WrapperLeft className="wrapper--small">
						{props.leftPanel}
					</WrapperLeft>
				</Box>
			</Grid>

			<Grid
				item
				xs={rightSize.xs ?? 12}
				sm={rightSize.sm ?? 6}
				sx={{
					minWidth: 0,
					...props.rightPanelSx,
				}}
			>
				<Box sx={panelShellSx}>
					<WrapperRight className="wrapper--half">
						{props.rightPanel}
					</WrapperRight>
				</Box>
			</Grid>
		</Grid>
	);
};
